import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { BaseSection, type BaseSectionProps } from '@/components/sections/BaseSection';
import { TitleHeading } from '@/components/helper/TitleHeading';
import BlogCard from '@/components/blog/BlogCard';
import { getFeaturedPost, getLatestPosts } from '@/utils/blog';
import '@/styles/blog.css';

export const Blog = (props: BaseSectionProps) => {
  const { t } = useTranslation();
  const { locale } = useParams();
  const navigate = useNavigate();
  const reducedMotion = usePrefersReducedMotion();

  const currentLocale = locale ?? 'en';
  const featured = getFeaturedPost(currentLocale);
  const latest = getLatestPosts(currentLocale, 2);

  const handleCardClick = (slug: string) => {
    navigate(`/${currentLocale}/blog/${slug}`);
  };

  return (
    <BaseSection {...props}>
      <div className="section-head-split mb-10 lg:mb-16">
        <div>
          <TitleHeading>
            {t('blog.title')}
          </TitleHeading>
          <h2 className="section-subtitle">
            {t('blog.subtitle')}
          </h2>
        </div>
        <p className="section-lede">{t('blog.description')}</p>
      </div>

      <div className="space-y-10 lg:space-y-14">
        {/* Featured Post */}
        {featured && (
          <BlogCard
            post={featured}
            featured
            onClick={() => handleCardClick(featured.slug)}
            index={0}
            inView={props.section.viewHook.inView}
          />
        )}

        {/* Latest Posts */}
        {latest.length > 0 && (
          <div className="blog-row-list">
            {latest.map((post, index) => (
              <BlogCard
                key={post.slug}
                post={post}
                onClick={() => handleCardClick(post.slug)}
                index={index + 1}
                inView={props.section.viewHook.inView}
              />
            ))}
          </div>
        )}

        {/* View All Posts Button */}
        <motion.div
          className="flex justify-start gap-3 pt-4"
          initial={{ opacity: 0, y: reducedMotion ? 0 : 20 }}
          animate={{
            opacity: props.section.viewHook.inView ? 1 : 0,
            y: props.section.viewHook.inView || reducedMotion ? 0 : 20,
          }}
          transition={{ duration: reducedMotion ? 0 : 0.5, ease: [0.16, 1, 0.3, 1], delay: reducedMotion ? 0 : 0.15 }}
        >
          <button
            className="button button-md button-dark min-h-11 inline-flex items-center justify-center"
            type="button"
            onClick={() => navigate(`/${currentLocale}/blog`)}
          >
            {t('blog.viewAllPosts')}
          </button>
          <button
            className="button button-md min-h-11 inline-flex items-center justify-center"
            type="button"
            onClick={() => navigate(`/${currentLocale}/blog?view=graph`)}
          >
            {t('blog.exploreAsGraph')}
          </button>
        </motion.div>
      </div>
    </BaseSection>
  );
};
